/**
 * Date utility functions for handling UTC to local time conversion
 * and consistent date formatting across the application
 */

/**
 * Formats a UTC date string to local date and time
 * @param utcDateString - UTC date string from the database
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted local date string
 */
export const formatToLocalDateTime = (
  utcDateString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!utcDateString) return 'N/A'
  
  try {
    const date = new Date(utcDateString)
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }
    
    return date.toLocaleString(undefined, { ...defaultOptions, ...options })
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid Date'
  }
}

/**
 * Formats a UTC date string to local date only (no time)
 * @param utcDateString - UTC date string from the database
 * @returns Formatted local date string
 */
export const formatToLocalDate = (utcDateString: string | null | undefined): string => {
  return formatToLocalDateTime(utcDateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Formats a UTC date string to local time only (no date)
 * @param utcDateString - UTC date string from the database
 * @returns Formatted local time string
 */
export const formatToLocalTime = (utcDateString: string | null | undefined): string => {
  return formatToLocalDateTime(utcDateString, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

/**
 * Formats a UTC date string to a relative time (e.g., "2 hours ago")
 * @param utcDateString - UTC date string from the database
 * @returns Relative time string
 */
export const formatToRelativeTime = (utcDateString: string | null | undefined): string => {
  if (!utcDateString) return 'N/A'
  
  try {
    const date = new Date(utcDateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    
    if (diffInSeconds < 60) {
      return 'Just now'
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400)
      return `${days} day${days !== 1 ? 's' : ''} ago`
    } else {
      // For dates older than a week, show the actual date
      return formatToLocalDate(utcDateString)
    }
  } catch (error) {
    console.error('Error formatting relative time:', error)
    return 'Invalid Date'
  }
}

/**
 * Formats the elapsed time between two UTC date strings as a compact duration
 * (e.g. "45s", "3m 42s", "1h 5m"). Omit endUtcDateString to measure against now
 * (for an in-progress interval).
 * @param startUtcDateString - UTC date string the interval started at
 * @param endUtcDateString - UTC date string the interval ended at, or omitted for "still ongoing"
 * @returns Compact duration string, or 'N/A' if startUtcDateString is missing/invalid
 */
export const formatDurationBetween = (
  startUtcDateString: string | null | undefined,
  endUtcDateString?: string | null
): string => {
  if (!startUtcDateString) return 'N/A'

  try {
    const start = new Date(startUtcDateString)
    const end = endUtcDateString ? new Date(endUtcDateString) : new Date()
    const totalSeconds = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000))

    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) return `${hours}h ${minutes}m`
    if (minutes > 0) return `${minutes}m ${seconds}s`
    return `${seconds}s`
  } catch (error) {
    console.error('Error formatting duration:', error)
    return 'Invalid Date'
  }
}

/**
 * Formats a UTC date string for display in tables or lists
 * Shows date and time in a compact format with timezone
 * @param utcDateString - UTC date string from the database
 * @returns Formatted compact date string with timezone
 */
export const formatToCompactDateTime = (utcDateString: string | null | undefined): string => {
  return formatToLocalDateTime(utcDateString, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  })
}

/**
 * Gets the user's timezone
 * @returns User's timezone string
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Formats a date for API requests (ensures UTC format)
 * @param date - Date object or date string
 * @returns ISO string in UTC format
 */
export const formatForAPI = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toISOString()
}
